import { isFrancePlatform } from '@/core/settings/utils';
import type { Attribute } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import type { Attribute as AttributeFromApi } from '@/domains/productDiscovery/ProductPage/interfaces/fromApi/msfProductPage.model';
import { getUnit } from '@/productDiscovery/ProductPage/common/processors/utils';
import {
  findRelevantClassifications,
  findSustainableClassifications,
} from '@/sustainability/productPage/sustainabilityAttributes';

export interface ExtraAttributesOptions {
  ean?: string;
  manufacturerSKU?: string;
  mmId?: number;
  productRef?: string;
}

const ATTRIBUTES_BLACKLIST = [
  // Carbon Score calculation attributes
  14515, 14516, 14517, 14583, 14584, 14585,
];
// helpers for warranty attribute
export const WARRANTY_ATTRIBUTE_ID = 537;

export const processAttributes = (
  attributes: AttributeFromApi[],
  options: ExtraAttributesOptions = {},
): Attribute[] => {
  // remove some attributes because someone said so
  const filteredAttributes = attributes.filter(({ id, value }) => {
    if (id === WARRANTY_ATTRIBUTE_ID) return parseFloat(value) !== 0; // filter out warranty with 0 years
    return !ATTRIBUTES_BLACKLIST.includes(id);
  });

  // make a map on the attributes with adding all the useful info
  const processedAttributes = filteredAttributes.map((attribute): Attribute => {
    if (attribute.id === WARRANTY_ATTRIBUTE_ID) {
      return {
        ...attribute,
        unit: attribute.unit && getUnit(attribute.unit, attribute.value),
        isWarranty: true,
      };
    }

    // todo: do better than this
    if (!isFrancePlatform()) {
      return attribute;
    }

    const classifications = findSustainableClassifications(attribute);
    if (classifications) {
      return {
        ...attribute,
        isSustainablyRelevant: true,
        sustainableType: classifications.type,
      };
    }

    const relevantClassification = findRelevantClassifications(attribute);
    if (relevantClassification) {
      return {
        ...attribute,
        isSustainablyRelevant: true,
      };
    }

    return attribute;
  });

  // add some product info as attributes because someone said so
  const { productRef, manufacturerSKU, mmId, ean } = options;
  const extraAttributes = [
    {
      name: 'productRef',
      value: productRef,
    },
    {
      name: 'mmId',
      value: mmId && `${mmId}`,
    },
    {
      name: 'manufacturerSKU',
      value: manufacturerSKU,
    },
    {
      name: 'ean',
      value: ean,
    },
  ].filter((attr) => !!attr.value) as Attribute[];

  return [...processedAttributes, ...extraAttributes];
};
